<template>
  <main class="photo-board">
    <section class="board-shell">
      <nav class="tabs"><button class="active">상담 보드 사진</button></nav>
      <div class="toolbar">
        <label class="patient-picker">
          <span>환자</span>
          <input v-model="patientQuery" placeholder="이름/차트/전화 검색" @focus="patientListOpen = true" />
          <div v-if="patientListOpen && patientQuery.trim()" class="patient-results">
            <button v-for="patient in filteredPatients" :key="patient.chartNo" type="button" @click="selectPatient(patient)">
              <strong>{{ patient.name }}</strong>
              <span>{{ patient.chartNo }} · {{ patient.phone || '-' }}</span>
            </button>
          </div>
        </label>
        <label class="file-button">
          사진 추가
          <input type="file" multiple accept="image/*" @change="pickImages" />
        </label>
        <button :disabled="!selectedImage" @click="deleteSelected">삭제</button>
        <button @click="saveBoard">저장</button>
      </div>

      <section class="summary-grid">
        <article><span>환자</span><strong>{{ currentPatient?.name || '미선택' }}</strong></article>
        <article><span>차트번호</span><strong>{{ currentPatient?.chartNo || '-' }}</strong></article>
        <article><span>사진</span><strong>{{ currentImages.length }}장</strong></article>
        <article><span>선택</span><strong>{{ selectedImage?.name || '-' }}</strong></article>
      </section>

      <section class="content-grid">
        <aside class="thumb-panel">
          <header>환자 사진 목록</header>
          <button
            v-for="image in currentImages"
            :key="image.id"
            type="button"
            :class="{ selected: selectedImageId === image.id }"
            @click="selectedImageId = image.id"
          >
            <img :src="image.url" alt="" />
            <span>{{ image.name }}</span>
          </button>
          <p v-if="!currentImages.length">사진을 추가하세요.</p>
        </aside>

        <section class="viewer-panel">
          <header>
            <strong>{{ selectedImage?.name || '사진 선택' }}</strong>
            <span>{{ selectedImage?.date || '' }}</span>
          </header>
          <div class="viewer">
            <img v-if="selectedImage" :src="selectedImage.url" alt="상담 사진" />
            <p v-else>왼쪽에서 사진을 선택하세요.</p>
          </div>
          <footer>
            <label><span>상담 메모</span><textarea v-model="memo" rows="3" placeholder="사진 상담 내용, 설명 메모" /></label>
          </footer>
        </section>
      </section>
    </section>
  </main>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

type Patient = { id?: number; chartNo: string; name: string; phone?: string };
type BoardImage = { id: number; chartNo: string; patientName: string; name: string; url: string; date: string; memo: string };

const STORAGE_KEY = 'bioa-counsel-photos';
const patients = ref<Patient[]>(loadPatients());
const images = ref<BoardImage[]>(readStorage<BoardImage[]>(STORAGE_KEY, []));
const currentPatient = ref<Patient | null>(patients.value[0] || null);
const patientQuery = ref(currentPatient.value ? `${currentPatient.value.name} ${currentPatient.value.chartNo}` : '');
const patientListOpen = ref(false);
const selectedImageId = ref<number | null>(null);
const memo = ref('');

const filteredPatients = computed(() => {
  const q = patientQuery.value.trim().toLowerCase();
  return patients.value.filter((p) => [p.name, p.chartNo, p.phone].join(' ').toLowerCase().includes(q)).slice(0, 40);
});
const currentImages = computed(() => images.value.filter((image) => !currentPatient.value || image.chartNo === currentPatient.value.chartNo).sort((a, b) => b.id - a.id));
const selectedImage = computed(() => currentImages.value.find((image) => image.id === selectedImageId.value) || currentImages.value[0] || null);

watch(selectedImage, (image) => {
  if (image) {
    selectedImageId.value = image.id;
    memo.value = image.memo || '';
  } else {
    memo.value = '';
  }
}, { immediate: true });

function selectPatient(patient: Patient) {
  currentPatient.value = patient;
  patientQuery.value = `${patient.name} ${patient.chartNo}`;
  patientListOpen.value = false;
  selectedImageId.value = null;
}

function pickImages(event: Event) {
  if (!currentPatient.value) return alert('환자를 선택하세요.');
  const input = event.target as HTMLInputElement;
  Array.from(input.files || []).forEach((file) => {
    const reader = new FileReader();
    reader.onload = () => {
      images.value.push({
        id: Date.now() + Math.random(),
        chartNo: currentPatient.value?.chartNo || '',
        patientName: currentPatient.value?.name || '',
        name: file.name,
        url: String(reader.result || ''),
        date: today(),
        memo: '',
      });
      persist();
    };
    reader.readAsDataURL(file);
  });
  input.value = '';
}

function saveBoard() {
  if (!selectedImage.value) return;
  const index = images.value.findIndex((image) => image.id === selectedImage.value?.id);
  if (index >= 0) {
    images.value[index] = { ...images.value[index], memo: memo.value };
    persist();
    alert('상담보드 사진 메모가 저장되었습니다.');
  }
}

function deleteSelected() {
  if (!selectedImage.value || !window.confirm('선택한 사진을 삭제할까요?')) return;
  images.value = images.value.filter((image) => image.id !== selectedImage.value?.id);
  selectedImageId.value = null;
  persist();
}

function persist() {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(images.value));
}

function loadPatients(): Patient[] {
  const stored = readStorage<Patient[]>('bioa-patients', []);
  if (stored.length) return stored.map((p) => ({ chartNo: p.chartNo || `P-${p.id || 'TEMP'}`, name: p.name || '환자', phone: p.phone || '' }));
  return [];
}

function readStorage<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) as T : fallback;
  } catch {
    return fallback;
  }
}

function today() {
  return new Date().toISOString().slice(0, 10);
}
</script>

<style scoped>
.photo-board{height:100%;min-height:0;overflow:hidden;background:#f6f9fb;color:#34404a;font-size:13px}.board-shell{height:100%;min-height:0;background:#fff;border-top:1px solid #d9e2ea;display:flex;flex-direction:column;overflow:hidden}.tabs{display:flex;gap:22px;height:38px;align-items:flex-end;padding:0 20px;border-bottom:1px solid #d9e2ea}.tabs button{height:38px;border:0;background:transparent;color:#2275d7;font-weight:900;position:relative}.tabs button:after{content:'';position:absolute;left:8px;right:8px;bottom:-1px;height:2px;background:#2275d7}.toolbar{height:52px;display:flex;align-items:center;gap:6px;padding:8px 16px;border-bottom:1px solid #e3eaf0;box-sizing:border-box}.toolbar button,.file-button{height:30px;border:0;border-radius:4px;background:#e8eef4;color:#3d4b57;padding:0 12px;font-weight:800;cursor:pointer;display:inline-flex;align-items:center}.toolbar button:disabled{opacity:.45}.file-button input{display:none}.patient-picker{position:relative;display:grid;grid-template-columns:auto 260px;align-items:center;gap:6px;color:#7b8994}.patient-picker input{height:30px;border:1px solid #cfd9e3;border-radius:4px;padding:0 8px}.patient-results{position:absolute;top:34px;left:36px;width:340px;max-height:240px;overflow:auto;border:1px solid #cfd9e3;background:#fff;box-shadow:0 14px 30px rgba(35,49,61,.16);z-index:5;padding:5px}.patient-results button{width:100%;height:auto;min-height:40px;background:#fff;text-align:left;display:grid;grid-template-columns:1fr auto}.summary-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:10px;padding:12px 16px;border-bottom:1px solid #e3eaf0;background:#fbfcfd}.summary-grid article{border:1px solid #dce5ec;background:#fff;border-radius:4px;padding:10px}.summary-grid span{display:block;color:#7b8994}.summary-grid strong{display:block;margin-top:4px;font-size:17px;color:#263746}.content-grid{min-height:0;flex:1;display:grid;grid-template-columns:300px minmax(0,1fr);overflow:hidden}.thumb-panel{min-height:0;border-right:1px solid #e3eaf0;background:#fbfcfd;padding:12px;box-sizing:border-box;overflow:auto;display:grid;align-content:start;gap:8px}.thumb-panel header{font-weight:900;color:#263746}.thumb-panel button{border:1px solid #e3eaf0;border-radius:4px;background:#fff;display:grid;grid-template-columns:72px minmax(0,1fr);align-items:center;gap:8px;padding:6px;text-align:left;cursor:pointer}.thumb-panel button:hover,.thumb-panel button.selected{border-color:#2f7bea;background:#eef6ff}.thumb-panel img{width:72px;height:54px;object-fit:cover;background:#edf2f7}.thumb-panel span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.thumb-panel p,.viewer p{margin:0;padding:16px;color:#8a97a3}.viewer-panel{min-height:0;display:flex;flex-direction:column;overflow:hidden}.viewer-panel header{height:42px;border-bottom:1px solid #e3eaf0;display:flex;align-items:center;justify-content:space-between;padding:0 16px}.viewer-panel header strong{color:#263746}.viewer-panel header span{color:#7b8994}.viewer{min-height:0;flex:1;display:flex;align-items:center;justify-content:center;background:#111;overflow:hidden}.viewer img{max-width:100%;max-height:100%;object-fit:contain}.viewer-panel footer{border-top:1px solid #d9e2ea;padding:12px 16px}.viewer-panel label{display:grid;gap:6px;color:#5f6e7b}.viewer-panel textarea{width:100%;border:1px solid #cfd9e3;border-radius:4px;padding:8px;box-sizing:border-box;resize:vertical}
</style>
